import React from "react";
import { makeStyles } from "tss-react/mui";

import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import InventoryIcon from '@mui/icons-material/Inventory';
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import RoomData from "../../../interfaces/robot/rooms/room-data";
import Base64Image from "../../misc/Base64Image";

const thumbnailMarginPx = 2;
const listedRoomImageWidthPx = 40;

const useStyles = makeStyles()((theme) => ({
  iconDiv: {
    width: `${listedRoomImageWidthPx - 2 * thumbnailMarginPx}px`,
    height: `${listedRoomImageWidthPx - 2 * thumbnailMarginPx}px`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  thumbnailImageDiv: {
    display: "flex",
    justifyContent: "center",
    margin: `${thumbnailMarginPx}px`,
    padding: "1px",
    width: "100%",
    height: "100%",
    border: "1px solid rgba(17,143,243, 0.25)",
    backgroundColor: "rgb(205,205,205)",
  },
  listedRoomLabel: {
    fontWeight: 500,
  },
  roomSelectOption: {
    display: "inline-flex",
    gap: theme.spacing(1),
    width: "minContent",
    alignItems: "center",
  },
  roomSelect: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  roomSelectContainer: {
    height: `${listedRoomImageWidthPx - 2 * thumbnailMarginPx + parseInt(theme.spacing(1), 10)}px`,
  },
}));

export const RoomMiniOption: React.FC<{ roomMetaInfo: RoomData }> = ({ roomMetaInfo }) => {
  const { classes } = useStyles();

  return (
    <React.Fragment>
      <div className={classes.roomSelectOption}>
        <div className={classes.iconDiv}>
          {roomMetaInfo.isArchived ? (
            <InventoryIcon color="action" />
          ) : roomMetaInfo.secondaryStation != null ? (
            <SwapHorizRoundedIcon color="action" />
          ) : (
            <div className={classes.thumbnailImageDiv}>
              <Base64Image
                imageString={roomMetaInfo.map}
                alt={`map ${roomMetaInfo.id}`}
                style={{ alignSelf: "center", maxWidth: "100%", maxHeight: "100%" }}
              />
            </div>
          )}
        </div>
        <Typography variant="body2" className={classes.listedRoomLabel}>
          {roomMetaInfo.name}
        </Typography>
      </div>
    </React.Fragment>
  );
};

const RoomSelect: React.FC<{
  roomIds: string[];
  availableRooms: RoomData[];
  onRoomIdsChange: (event: SelectChangeEvent<string[]>) => void;
}> = ({ roomIds, availableRooms, onRoomIdsChange }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <FormControl fullWidth variant="standard">
      <InputLabel id="excludedDate-rooms-label">{t("excludedDate-rooms-title")}</InputLabel>
      <Select
        labelId="excludedDate-rooms-label"
        multiple
        fullWidth
        className={classes.roomSelectContainer}
        value={roomIds}
        data-testid="select-rooms-id"
        onChange={onRoomIdsChange}
        renderValue={(selected: any) => (
          <div className={classes.roomSelect}>
            {selected.map((roomId: string) => {
              const roomMetaInfo = availableRooms.find((r) => r.id === roomId);
              if (roomMetaInfo !== undefined) {
                return <RoomMiniOption key={roomMetaInfo.id} roomMetaInfo={roomMetaInfo} />;
              }
              return null;
            })}
          </div>
        )}
      >
        {availableRooms.map((roomMetaInfo) => (
          <MenuItem value={roomMetaInfo.id} key={roomMetaInfo.id}>
            <RoomMiniOption roomMetaInfo={roomMetaInfo} />
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>
        {roomIds.length === 0 ? t("excludedDate-global-message") : t("excludedDate-selectedRooms-message")}
      </FormHelperText>
    </FormControl>
  );
};

export default RoomSelect;
